import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import kairosLogoWithName from "../images/kairos_logo_with_name.png";
import "./PublicDocShell.css";

const DOC_LINKS = [
  { to: "/help", label: "Help", end: true },
  { to: "/terms-of-service", label: "Terms", end: false },
  { to: "/privacy-policy", label: "Privacy", end: false },
  { to: "/cookie-policy", label: "Cookies", end: false },
];

export interface PublicDocShellProps {
  eyebrow: string;
  title: React.ReactNode;
  subtitle?: string;
  children: React.ReactNode;
}

export const PublicDocShell: React.FC<PublicDocShellProps> = ({
  eyebrow,
  title,
  subtitle,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
    } else {
      navigate(isAuthenticated ? "/dashboard" : "/login");
    }
  };

  return (
    <div className="public-doc-shell">
      <div className="public-doc-backdrop" aria-hidden="true" />

      <header className="public-doc-header">
        <div className="public-doc-header-row1">
          <button
            type="button"
            className="public-doc-back-button"
            onClick={handleBack}
            aria-label="Go back"
          >
            <svg
              className="public-doc-back-icon"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span>Back</span>
          </button>
          <Link
            to="/"
            className="public-doc-logo-link"
            aria-label="Kairos home"
          >
            <img src={kairosLogoWithName} alt="" className="public-doc-logo" />
          </Link>
          <Link to="/login" className="public-doc-header-login">
            Log in
          </Link>
        </div>
        <nav className="public-doc-nav" aria-label="Help and legal pages">
          {DOC_LINKS.map(({ to, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `public-doc-nav-link${isActive ? " public-doc-nav-link--active" : ""}`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </header>

      <main className="public-doc-main">
        <div className="public-doc-container">
          <div className="public-doc-hero">
            <p className="public-doc-eyebrow">{eyebrow}</p>
            <h1 className="public-doc-title">{title}</h1>
            {subtitle ? (
              <p className="public-doc-subtitle">{subtitle}</p>
            ) : null}
          </div>
          {children}
        </div>
      </main>
    </div>
  );
};
